import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@components/AppIcon";
import Button from "@components/ui/Button";

const TeamIntroduction = () => {
  const [teamValues, setTeamValues] = useState([]);

  useEffect(() => {
    const fetchTeamValues = async () => {
      try {
        const res = await fetch("/api/about_us");
        const data = await res.json();

        if (Array.isArray(data) && data.length > 0) {
          const aboutEntry = data[0]; 
          const sections = aboutEntry.sections || [];

          // Find "our_values" section
          const valuesSection = sections.find(
            (s) => s.type === "our_values" && s.enabled
          );

          if (valuesSection && valuesSection.our_values) {
            const mappedValues = valuesSection.our_values
              .filter((v) => v.enabled) 
              .map((v) => ({
                title: v.name,
                description: v.description?.[0]?.content?.[0]?.text || "",
                icon: guessIcon(v.name),
              }));
            setTeamValues(mappedValues);
          }
        }
      } catch (error) {
        console.error("Error fetching team values:", error);
      }
    };

    fetchTeamValues();
  }, []);

  // Optional: Map value names to icons
  const guessIcon = (name) => {
    if (name.includes("Patient")) return "Heart";
    if (name.includes("Innovation")) return "Zap";
    if (name.includes("Excellence") || name.includes("Integrity"))
      return "Award";
    if (name.includes("Community")) return "Users";
    if (name.includes("Learning")) return "BookOpen";
    return "Star";
  };

  if (teamValues.length === 0) {
    return null; // hide entire section if nothing enabled
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        {/* Team Values */}
        <div className="bg-[#f5f6fa] rounded-3xl p-8 lg:p-12 mb-12 border border-[#08005f] border-opacity-10">
          <div className="text-center mb-12">
            <h3
              className="font-heading font-bold text-2xl lg:text-3xl mb-4"
              style={{ color: "#08005f" }}
            >
              Our Team Values
            </h3>
            <p className="text-text-secondary leading-relaxed max-w-2xl mx-auto">
              The values that guide our hospital, shaping every patient
              interaction, treatment plan, and our long-term commitment to eye
              care excellence in Kenya.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {teamValues.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center hover:bg-[#e8eafd] hover:medical-shadow medical-transition border border-[#08005f] border-opacity-10"
              >
                <div className="w-16 h-16 bg-[#08005f]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon
                    name={value.icon}
                    size={28}
                    color="#08005f"
                    strokeWidth={2}
                  />
                </div>
                <h4
                  className="font-heading font-semibold text-lg mb-3"
                  style={{ color: "#08005f" }}
                >
                  {value.title}
                </h4>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-[#08005f]/5 rounded-2xl p-8 lg:p-12">
            <h3
              className="font-heading font-bold text-2xl lg:text-3xl mb-4"
              style={{ color: "#08005f" }}
            >
              Ready to Meet Our Team?
            </h3>
            <p className="text-text-secondary leading-relaxed mb-8 max-w-2xl mx-auto">
              Discover detailed profiles of our founders and expert doctors,
              including their specialties, decades of experience, and patient
              success stories. Find the right specialist for your vision needs
              today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/our-team-expert-network-personal-connections">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Users"
                  iconPosition="left"
                  iconSize={20}
                  className="bg-[#08005f] hover:bg-[#eb2227] border-none text-white"
                >
                  View Full Team
                </Button>
              </Link>
              <Link to="/book-appointment-guided-care-journey">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                  iconSize={20}
                  className="border-[#08005f] text-[#08005f] hover:bg-[#eb2227] hover:text-white"
                >
                  Book Appointment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamIntroduction;
